import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

import { SfLog } from '@app/adapters/logger'
import {
  deleteQueryParam,
  setQueryParam,
  setQueryParams,
} from '@app/utils/url-helpers'
import type { Attachment } from '../../sidebar-file-attachments/types'
import type { DocumentPreviewResponse } from '@app/shared/document-preview/types'
import { getDocumentPreview } from '@app/shared/document-preview/api'

export const useSidebarFilePreviewStore = defineStore(
  'sidebarFilePreview',
  () => {
    const attachment = ref<Attachment | null>(null)
    const documentPreview = ref<DocumentPreviewResponse | null>(null)

    async function fetchDocumentPreview(): Promise<void> {
      if (!attachment.value?.document_id) {
        SfLog.warn(
          'No document ID found for the attachment. Cannot fetch document preview.'
        )
        return
      }

      try {
        const response = await getDocumentPreview(attachment.value.document_id)

        if (!response) {
          SfLog.error('Empty document preview response received')
          return
        }

        documentPreview.value = response
      } catch (error) {
        SfLog.error('Failed to fetch document preview for the attachment: ', {
          error,
        })
        return
      }
    }

    const setAttachment = (newAttachment: Attachment | null): void => {
      attachment.value = newAttachment
    }

    const syncFilePreviewDrawerUrlParams = (): void => {
      if (attachment.value) {
        setQueryParams({
          document_id: attachment.value.document_id,
          document_name: attachment.value.name,
          document_size: attachment.value.size,
        })
        if (!attachment.value.previewable) {
          setQueryParam('document_previewable', 'false')
        }
      } else {
        deleteQueryParam('document_id')
        deleteQueryParam('document_name')
        deleteQueryParam('document_size')
        deleteQueryParam('document_previewable')
      }
    }

    watch(
      attachment,
      async (newValue, oldValue) => {
        if (newValue && newValue.document_id !== oldValue?.document_id) {
          await fetchDocumentPreview()
        }
      },
      { immediate: true, deep: true }
    )

    return {
      attachment,
      documentPreview,
      setAttachment,
      syncFilePreviewDrawerUrlParams,
    }
  }
)
